import { useEffect, useMemo, useState } from "react";
import { BadgeCheck, ShieldCheck } from "lucide-react";
import type { ApplicationFormValues } from "@/lib/applications";

export function ApplicationBrandingPreview({
  values,
  eVerify,
  projectName,
}: {
  values: ApplicationFormValues;
  eVerify: boolean;
  projectName: string;
}) {
  const [brokenLogo, setBrokenLogo] = useState(false);
  useEffect(() => setBrokenLogo(false), [values.logoUrl]);
  const safeLogo = useMemo(() => safeImageURL(values.logoUrl), [values.logoUrl]);
  const initials = (values.name.trim() || "Application")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  const branding = values.branding;

  return (
    <section className="overflow-hidden rounded-lg border border-border-standard bg-surface-container-lowest" aria-label="Hosted authentication preview">
      <div className="flex items-center justify-between border-b border-border-standard bg-surface-container-low px-3 py-2 text-[11px] text-text-muted">
        <span className="font-mono uppercase tracking-label">Hosted preview</span>
        <span className="inline-flex items-center gap-1 text-success">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
          TinyBase-controlled trust UI
        </span>
      </div>
      <div
        className="flex min-h-[28rem] items-center justify-center p-5"
        style={{ backgroundColor: safeColor(branding.background_color, "#FFFFFF") }}
      >
        <div
          className={
            branding.display_mode === "card"
              ? "w-full max-w-sm rounded-lg border border-border-standard bg-surface-container-lowest p-6 shadow-none"
              : "w-full max-w-sm p-4"
          }
          style={{ color: safeColor(branding.text_color, "#1A1C1C") }}
        >
          <div className="mb-4 flex items-center gap-3">
            {safeLogo && !brokenLogo ? (
              <img
                src={safeLogo}
                alt=""
                className="h-11 w-11 rounded-lg border border-border-standard bg-surface-container-lowest object-contain p-1"
                onError={() => setBrokenLogo(true)}
                referrerPolicy="no-referrer"
              />
            ) : (
              <div
                className="flex h-11 w-11 items-center justify-center rounded-sm text-sm font-semibold text-on-primary"
                style={{ backgroundColor: safeColor(branding.accent_color, "#0054CA") }}
                aria-label="Application logo fallback"
              >
                {initials || "A"}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 font-semibold">
                <span className="truncate">{values.name.trim() || "Your application"}</span>
                {eVerify ? (
                  <span className="inline-flex shrink-0 items-center text-secondary" title="Verified by TinyBase">
                    <BadgeCheck className="h-4 w-4" aria-hidden />
                    <span className="sr-only">Verified by TinyBase</span>
                  </span>
                ) : null}
              </div>
              <div className="text-xs opacity-65">Project: {projectName || "Current Project"}</div>
            </div>
          </div>
          <h2 className="text-lg font-semibold">Continue to {values.name.trim() || "your application"}</h2>
          <p className="mt-1 text-sm opacity-70">
            {values.description.trim() || "Sign in securely with your Project account."}
          </p>
          <div className="mt-5 space-y-2">
            <label className="block text-xs font-medium" htmlFor="preview-email">Email</label>
            <input
              id="preview-email"
              disabled
              placeholder="you@example.com"
              className="w-full rounded-sm border border-border-standard bg-surface-container-lowest px-3 py-2 text-sm text-text-primary disabled:opacity-80"
            />
            <button
              type="button"
              className="w-full rounded-sm px-3 py-2 text-sm font-semibold text-on-primary"
              style={{ backgroundColor: safeColor(branding.primary_color, "#964900") }}
            >
              Continue
            </button>
          </div>
          <div className="mt-5 flex items-center justify-center gap-1 text-[11px] opacity-65">
            <ShieldCheck className="h-3 w-3" aria-hidden /> Powered by TinyBase
          </div>
        </div>
      </div>
    </section>
  );
}

function safeColor(value: string, fallback: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
}

function safeImageURL(value: string): string | null {
  if (!value.trim()) return null;
  try {
    const parsed = new URL(value);
    if (parsed.username || parsed.password || parsed.hash || value.includes("*") || value.includes("\\")) {
      return null;
    }
    const loopback = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
    const authority = value.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/([^/?#]+)/)?.[1] ?? "";
    const hasExplicitPort = /:\d+$/.test(authority);
    const safeProtocol =
      parsed.protocol === "https:" ||
      (parsed.protocol === "http:" && loopback && hasExplicitPort);
    return safeProtocol ? parsed.toString() : null;
  } catch {
    return null;
  }
}
